/**
 * eventState — the single derivation of "what state is this event in".
 *
 * Availability was being worked out independently by the card, the detail page
 * and the ticket rail, which is how a sold-out event could still render as
 * available in a list. Everything that needs to know now asks here.
 */

// Canonical pricing helper. When tiers exist the lowest one is the headline
// price, which is why it is a min and not the first tier.
export function getEventEffectivePrice(event) {
  if (event?.vibanaTickets?.enabled && event.vibanaTickets.types?.length > 0) {
    return Math.min(...event.vibanaTickets.types.map(t => Number(t.price) || 0));
  }
  return Number(event?.ticketInfo?.price ?? 0);
}

export function isEventFree(event) {
  if (!event) return false;
  if (event.ticketInfo?.isFree === true) return true;
  return getEventEffectivePrice(event) <= 0;
}

/**
 * Past means the event is over, not merely started — a 6-hour show is still
 * happening two hours in. Falls back to a grace period after the start time
 * when no end date was recorded.
 */
export function isEventPast(event, now = new Date()) {
  if (!event) return false;

  if (event.endDate) {
    const end = new Date(event.endDate);
    const start = new Date(event.date || 0);
    if (!Number.isNaN(end.getTime()) && end > start) return end < now;
  }

  if (!event.date) return false;
  const start = new Date(event.date);
  if (Number.isNaN(start.getTime())) return false;

  const GRACE_MS = 6 * 60 * 60 * 1000;
  return start.getTime() + GRACE_MS < now.getTime();
}

export function isEventToday(event, now = new Date()) {
  if (!event?.date) return false;
  const d = new Date(event.date);
  if (Number.isNaN(d.getTime())) return false;
  return d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
}

const LOW_STOCK_THRESHOLD = 20;

/**
 * Ticket availability across all tiers.
 *
 * Only counts tiers that declare a quantity: an unlimited tier cannot sell out,
 * and treating quantity 0 as "none left" would mark every uncapped event sold
 * out. An event with no capped tiers is therefore never sold out.
 */
export function getTicketAvailability(event) {
  const types = event?.vibanaTickets?.enabled ? (event.vibanaTickets.types || []) : [];
  const capped = types.filter(t => Number(t.quantity) > 0);

  if (capped.length === 0) {
    return { tracked: false, total: 0, sold: 0, remaining: null, isSoldOut: false, isLowStock: false };
  }

  const total = capped.reduce((sum, t) => sum + Number(t.quantity || 0), 0);
  const sold = capped.reduce((sum, t) => sum + Number(t.sold || 0), 0);
  const remaining = Math.max(0, total - sold);

  return {
    tracked: true,
    total,
    sold,
    remaining,
    isSoldOut: remaining === 0,
    isLowStock: remaining > 0 && remaining <= LOW_STOCK_THRESHOLD,
  };
}

/**
 * Everything a card or CTA needs, derived once.
 *
 * `status` is the single badge worth showing, resolved by precedence rather
 * than letting a card stack "Tonight" on top of "Sold out": over beats
 * unavailable, unavailable beats urgency, urgency beats promotion.
 */
export function getEventState(event, now = new Date()) {
  const free = isEventFree(event);
  const past = isEventPast(event, now);
  const today = isEventToday(event, now);
  const availability = getTicketAvailability(event);
  const price = getEventEffectivePrice(event);

  const hasTiers = !!event?.vibanaTickets?.enabled && (event.vibanaTickets.types?.length || 0) > 1;
  const priceLabel = free ? 'Free' : `${hasTiers ? 'from ' : ''}$${price}`;

  // A past event's stock is irrelevant, so soldOut is suppressed once it is over.
  const soldOut = !past && availability.isSoldOut;
  const lowStock = !past && !soldOut && availability.isLowStock;

  let status = null;
  if (past) status = 'ended';
  else if (soldOut) status = 'soldout';
  else if (today) status = 'tonight';
  else if (lowStock) status = 'lowstock';
  else if (event?.isFeatured) status = 'featured';

  return {
    isFree: free,
    isPast: past,
    isToday: today,
    isSoldOut: soldOut,
    isLowStock: lowStock,
    isFeatured: !!event?.isFeatured,
    remaining: availability.remaining,
    tracked: availability.tracked,
    price,
    hasTiers,
    priceLabel,
    status,
  };
}
